import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import dataService from 'services/dataService';
import {
  apiPapCasesManagement,
  apiPapCasesManagementOverview,
} from 'services/dataService/resources/oneplatform/api';

import type {
  IPapWorkplaceCase,
  IPapCasesManagementResponse,
  IPapCasesManagementOverviewResponse,
  PapCaseStatusValue,
  PapWorkplaceStatusFilter,
} from '../types';

const STATUS_MAP: Record<string, PapCaseStatusValue> = {
  'in-review': 'inReview',
  'needs-info': 'needsInfo',
};

const normalizeStatus = (status: string): PapCaseStatusValue =>
  (STATUS_MAP[status] ?? status) as PapCaseStatusValue;

const toApiStatus = (filter: PapWorkplaceStatusFilter): string | undefined => {
  if (filter === 'all') return undefined;
  const map: Record<PapCaseStatusValue, string> = {
    inReview: 'in-review',
    needsInfo: 'needs-info',
    submitted: 'submitted',
    approved: 'approved',
    denied: 'denied',
  };
  return map[filter];
};

const SORT_KEY_MAP: Record<string, string> = {
  specialist: 'isSpecialist',
  status: 'status',
};

const mapToCase = (
  item: IPapCasesManagementResponse['items'][number],
): IPapWorkplaceCase => ({
  id: item.guid,
  caseName: item.name,
  patientId: item.patientGuid,
  patientName: `${item.patient.firstName} ${item.patient.lastName}`,
  specialist: item.isSpecialist ? 'Yes' : 'No',
  status: normalizeStatus(item.status),
  note: item.reviewerNotes,
  lastUpdate: item.updatedAt,
});

interface FetchParams {
  searchTerm?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
}

const fetchManagementCases = async (params: FetchParams) => {
  const url = apiPapCasesManagement({
    items: 30,
    page: params.page ?? 1,
    ...(params.searchTerm ? { searchTerm: params.searchTerm } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.sortBy ? { sortBy: params.sortBy } : {}),
    ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
  });
  const { data, error } =
    await dataService.getList<IPapCasesManagementResponse>(url);
  if (error) throw new Error(error);
  return {
    cases: data?.items.map(mapToCase) ?? [],
    total: data?.count ?? 0,
  };
};

export const usePapWorkplace = () => {
  const [statusFilter, setStatusFilter] =
    useState<PapWorkplaceStatusFilter>('all');
  const [selectedCase, setSelectedCase] = useState<IPapWorkplaceCase | null>(
    null,
  );

  const handleOpenCase = (record: IPapWorkplaceCase) => {
    setSelectedCase(record);
  };

  const handleCloseCase = () => {
    setSelectedCase(null);
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>(
    undefined,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      if (value.length === 0 || value.length >= 2) {
        setSearchQuery(value);
        setDebouncedInputValue(value);
        setCurrentPage(1);
      }
    }, 400),
    [],
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const handleSearchSelect = (value: string) => {
    setInputValue(value);
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleSearchClear = () => {
    setInputValue('');
    setSearchQuery('');
    setDebouncedInputValue('');
    setCurrentPage(1);
  };

  const apiStatus = toApiStatus(statusFilter);

  const {
    data: mainData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      'papWorkplaceManagement',
      searchQuery,
      apiStatus,
      sortBy,
      sortOrder,
      currentPage,
    ],
    queryFn: () =>
      fetchManagementCases({
        searchTerm: searchQuery || undefined,
        status: apiStatus,
        sortBy,
        sortOrder,
        page: currentPage,
      }),
    keepPreviousData: true,
  });

  const cases = mainData?.cases ?? [];
  const total = mainData?.total ?? 0;

  const { data: suggestionData } = useQuery({
    queryKey: ['papWorkplaceSuggestions', debouncedInputValue],
    queryFn: () =>
      fetchManagementCases({ searchTerm: debouncedInputValue || undefined }),
    keepPreviousData: true,
  });
  const suggestionResults = suggestionData?.cases ?? [];

  const searchSuggestions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    suggestionResults.forEach((c) => {
      if (!seen.has(c.patientName)) {
        seen.add(c.patientName);
        opts.push({ value: c.patientName, label: c.patientName });
      }
      if (!seen.has(c.caseName)) {
        seen.add(c.caseName);
        opts.push({ value: c.caseName, label: c.caseName });
      }
    });
    return opts.slice(0, 8);
  }, [suggestionResults]);
  const { data: overviewData } = useQuery({
    queryKey: ['papWorkplaceManagementOverview', searchQuery],
    queryFn: async () => {
      const url = apiPapCasesManagementOverview(
        searchQuery ? { searchTerm: searchQuery } : undefined,
      );
      const { data, error } =
        await dataService.getList<IPapCasesManagementOverviewResponse>(url);
      if (error) throw new Error(error);
      return data;
    },
    keepPreviousData: true,
  });

  const statusCounts = useMemo(
    () => ({
      all:
        (overviewData?.['in-review'] ?? 0) +
        (overviewData?.['needs-info'] ?? 0) +
        (overviewData?.submitted ?? 0) +
        (overviewData?.approved ?? 0) +
        (overviewData?.denied ?? 0),
      inReview: overviewData?.['in-review'] ?? 0,
      needsInfo: overviewData?.['needs-info'] ?? 0,
      submitted: overviewData?.submitted ?? 0,
      approved: overviewData?.approved ?? 0,
      denied: overviewData?.denied ?? 0,
    }),
    [overviewData],
  );

  const handleSortChange = (
    columnKey: string | undefined,
    order: 'ascend' | 'descend' | null | undefined,
  ) => {
    const apiKey = columnKey ? SORT_KEY_MAP[columnKey] ?? columnKey : undefined;
    setSortBy(order ? apiKey : undefined);
    let apiOrder: 'ASC' | 'DESC' | undefined;
    if (order) {
      const invert = apiKey === 'isSpecialist';
      if (order === 'ascend') apiOrder = invert ? 'DESC' : 'ASC';
      else if (order === 'descend') apiOrder = invert ? 'ASC' : 'DESC';
    }
    setSortOrder(apiOrder);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (filter: PapWorkplaceStatusFilter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  return {
    cases,
    total,
    isLoading: isLoading || isFetching,
    statusFilter,
    setStatusFilter: handleStatusFilterChange,
    currentPage,
    setCurrentPage,
    inputValue,
    searchQuery,
    setSearchQuery,
    handleInputChange,
    handleSearchSelect,
    handleSearchClear,
    searchSuggestions,
    statusCounts,
    handleSortChange,
    selectedCase,
    handleOpenCase,
    handleCloseCase,
  };
};
